import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Search } from "lucide-react";
import { apiService } from "../../services/apiService";
import { SpinnerCustom } from "../ui/Spinner";

import CustomSelect from "../ui/CustomSelect";
const FIELD_GROUPS = [
  {
    id: "identity",
    label: "Identity",
    fields: ["circuit_id", "provider", "provider_circuit_id", "billing_account", "works_with_circuit_id"],
  },
  {
    id: "service",
    label: "Service and status",
    fields: ["category", "service_type", "status", "bandwidth", "last_mile_provider", "notes", "follow_up_date"],
  },
  {
    id: "address",
    label: "Address",
    fields: ["street_address", "city", "state", "zip", "country"],
  },
  {
    id: "money",
    label: "Money and term",
    fields: ["mrc", "contract_term", "end_of_term"],
  },
  {
    id: "contact",
    label: "Contact",
    fields: ["first_name", "last_name", "email", "phone"],
  },
];

const FIELD_LABELS = {
  circuit_id: "Circuit ID",
  provider: "Provider",
  provider_circuit_id: "Provider circuit ID",
  billing_account: "Billing account",
  works_with_circuit_id: "Works with circuit ID",
  category: "Category",
  service_type: "Service type",
  status: "Status",
  bandwidth: "Bandwidth",
  last_mile_provider: "Last mile provider",
  notes: "Notes",
  follow_up_date: "Follow up date",
  street_address: "Street address",
  city: "City",
  state: "State",
  zip: "ZIP",
  country: "Country",
  mrc: "MRC",
  contract_term: "Contract term (months)",
  end_of_term: "End of term",
  first_name: "First name",
  last_name: "Last name",
  email: "Email",
  phone: "Phone",
};

function humanFieldLabel(field) {
  const key = String(field || "");
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function pillClass(field) {
  const f = String(field || "").toLowerCase();
  if (["circuit_id", "provider", "provider_circuit_id", "works_with_circuit_id", "billing_account"].includes(f)) {
    return "bg-[#fbe4e4] text-[#a51c1c]";
  }
  if (["street_address", "city", "state", "zip", "country"].includes(f)) {
    return "bg-[#e8eef8] text-[#2c5fae]";
  }
  if (["mrc", "contract_term", "end_of_term"].includes(f)) {
    return "bg-[#e8f3ea] text-[#1f6b34]";
  }
  if (["first_name", "last_name", "email", "phone"].includes(f)) {
    return "bg-[#fbf1e0] text-[#b8791e]";
  }
  return "bg-[var(--sunken,#eeece7)] text-[var(--muted,#6b6a66)]";
}

function chipLabel(row, rowsForField) {
  const sameProvider = rowsForField.filter((r) => r.provider === row.provider);
  const categories = new Set(sameProvider.map((r) => r.category));
  if (categories.size > 1 && row.category) {
    return `${row.provider} · ${row.category} ${row.input}`;
  }
  return `${row.provider} ${row.input}`;
}

function normalizeLayouts(data) {
  const raw = data?.mappings || [];
  if (!raw.length) return [];
  if (raw[0]?.mappings) {
    return raw.map((group) => ({
      provider: group.provider,
      category: group.category,
      rows: (group.mappings || []).map((m) => ({
        input: m.input_column_name,
        field: m.inventory_column_name,
      })),
    }));
  }
  const map = new Map();
  raw.forEach((m) => {
    const key = `${m.provider}||${m.category}`;
    if (!map.has(key)) {
      map.set(key, { provider: m.provider, category: m.category, rows: [] });
    }
    map.get(key).rows.push({
      input: m.input_column_name,
      field: m.inventory_column_name,
    });
  });
  return [...map.values()];
}

export default function ImportFieldMappingsPanel({ onBack }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [layouts, setLayouts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [providerFilter, setProviderFilter] = useState("");
  const [viewMode, setViewMode] = useState("provider");
  const [activeGroups, setActiveGroups] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiService
      .getColumnMapping(undefined, undefined, undefined, "grouped")
      .then((res) => {
        if (cancelled) return;
        setLayouts(normalizeLayouts(res?.data || res));
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || "Failed to load field mappings");
        setLayouts([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const providers = useMemo(
    () => [...new Set(layouts.map((l) => l.provider).filter(Boolean))].sort(),
    [layouts]
  );

  const uniqueAppFields = useMemo(() => {
    const set = new Set();
    layouts.forEach((l) => l.rows.forEach((r) => r.field && set.add(r.field)));
    return set.size;
  }, [layouts]);

  const toggleGroup = (id) => {
    setActiveGroups((prev) =>
      prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]
    );
  };

  const fieldInActiveGroups = (field) => {
    if (!activeGroups.length) return true;
    return activeGroups.some((gid) =>
      (FIELD_GROUPS.find((g) => g.id === gid)?.fields || []).includes(String(field || "").toLowerCase())
    );
  };

  const filteredLayouts = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return layouts
      .filter((l) => !providerFilter || l.provider === providerFilter)
      .map((l) => ({
        ...l,
        rows: l.rows.filter((r) => {
          if (!fieldInActiveGroups(r.field)) return false;
          if (!q) return true;
          return (
            String(r.input || "").toLowerCase().includes(q) ||
            String(r.field || "").toLowerCase().includes(q)
          );
        }),
      }))
      .filter((l) => l.rows.length > 0);
  }, [layouts, providerFilter, searchTerm, activeGroups]);

  const groupedAppFields = useMemo(() => {
    const byField = new Map();
    filteredLayouts.forEach((l) => {
      l.rows.forEach((r) => {
        const key = String(r.field || "").trim() || "—";
        if (!byField.has(key)) byField.set(key, []);
        byField.get(key).push({ ...r, provider: l.provider, category: l.category });
      });
    });

    const used = new Set();
    const sections = FIELD_GROUPS.map((group) => ({
      ...group,
      items: group.fields
        .filter((field) => byField.has(field))
        .map((field) => {
          used.add(field);
          return { field, rows: byField.get(field) };
        }),
    })).filter((section) => section.items.length > 0);

    const leftover = [...byField.keys()].filter((field) => !used.has(field));
    if (leftover.length) {
      sections.push({
        id: "other",
        label: "Other",
        items: leftover.sort().map((field) => ({ field, rows: byField.get(field) })),
      });
    }
    return sections;
  }, [filteredLayouts]);

  const countryDefaultProviders = useMemo(() => {
    const withCountry = new Set();
    layouts.forEach((l) => {
      if (l.rows.some((r) => String(r.field).toLowerCase() === "country")) {
        withCountry.add(l.provider);
      }
    });
    return providers.filter((p) => !withCountry.has(p));
  }, [layouts, providers]);

  return (
    <div className="min-h-screen pt-[80px] bg-[var(--ground,#f5f4f1)]">
      <div className="w-full px-5 py-2 pb-14">
        <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4 mb-5">
          <div>
            <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[var(--accent,#a51c1c)]">
              Client Administration
            </p>
            <h1 className="mt-1 text-[26px] font-semibold leading-tight text-[var(--ink,#1c1c1a)]">
              Import Field Mappings
            </h1>
            <p className="mt-2 max-w-3xl text-[13px] text-[var(--muted,#6b6a66)]">
              How each provider's export file lands in AssetLedger. The left column is the header as it appears in the provider's file; the right column is the inventory field it fills. Columns not listed are ignored. Headers are matched case insensitively after trimming spaces.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2.5">
            <div className="inline-flex items-stretch overflow-hidden rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white shadow-sm">
              <div className="px-3 py-2 border-r border-[var(--line,#e4e2dc)]">
                <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                  Providers
                </label>
                <div className="mt-0.5 h-7 inline-flex items-center text-[13px] font-semibold tabular-nums">
                  {providers.length}
                </div>
              </div>
              <div className="px-3 py-2 border-r border-[var(--line,#e4e2dc)]">
                <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                  File layouts
                </label>
                <div className="mt-0.5 h-7 inline-flex items-center text-[13px] font-semibold tabular-nums">
                  {layouts.length}
                </div>
              </div>
              <div className="px-3 py-2">
                <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                  App fields
                </label>
                <div className="mt-0.5 h-7 inline-flex items-center text-[13px] font-semibold tabular-nums">
                  {uniqueAppFields}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={onBack}
              className="inline-flex h-[52px] items-center gap-2 rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-3.5 text-[13px] font-medium text-[var(--ink,#1c1c1a)] shadow-sm hover:bg-[var(--sunken,#eeece7)]"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to import
            </button>
          </div>
        </div>

        <div className="mb-4 rounded-[10px] border border-[#f1e0c4] bg-[#fbf1e0] px-4 py-3 text-[13px] text-[#8a5a12]">
          Country defaults to US. Only Lumen and GoTo files carry a country column. For every other provider the country is set to US on import because the file has no mapping for it.
        </div>

        <div className="mb-3 rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-4 py-3 flex flex-col lg:flex-row lg:items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--faint,#9a9a95)] pointer-events-none" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="File column or app field"
                className="h-[38px] w-full pl-9 pr-3 rounded-md border border-[var(--line-strong,#d5d3cc)] text-[13px] focus:outline-none focus:ring-2 focus:ring-[var(--accent,#a51c1c)]/25 focus:border-[var(--accent,#a51c1c)]"
              />
            </div>
          </div>
          <div className="min-w-[10rem]">
            <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1">
              Provider
            </label>
            <CustomSelect
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value)}
              className="h-[38px] w-full rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-[var(--muted,#6b6a66)]/20 focus:border-[var(--muted,#6b6a66)]"
            >
              <option value="">All</option>
              {providers.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </CustomSelect>
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1">
              View
            </label>
            <div className="inline-flex overflow-hidden rounded-md border border-[var(--line-strong,#d5d3cc)]">
              <button
                type="button"
                onClick={() => setViewMode("provider")}
                className={`h-[38px] px-3 text-[13px] font-medium ${
                  viewMode === "provider"
                    ? "bg-[var(--accent,#a51c1c)] text-white"
                    : "bg-white text-[var(--ink,#1c1c1a)]"
                }`}
              >
                By provider
              </button>
              <button
                type="button"
                onClick={() => setViewMode("field")}
                className={`h-[38px] px-3 text-[13px] font-medium ${
                  viewMode === "field"
                    ? "bg-[var(--accent,#a51c1c)] text-white"
                    : "bg-white text-[var(--ink,#1c1c1a)]"
                }`}
              >
                By app field
              </button>
            </div>
          </div>
          <p className="text-[12px] text-[var(--muted,#6b6a66)] lg:ml-auto pb-2 whitespace-nowrap">
            {filteredLayouts.length} of {layouts.length} layouts
          </p>
        </div>

        <div className="mb-4 flex flex-wrap gap-4">
          {FIELD_GROUPS.map((g) => {
            const on = activeGroups.includes(g.id);
            return (
              <label key={g.id} className="inline-flex items-center gap-1.5 text-[12px] text-[var(--muted,#6b6a66)] cursor-pointer">
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggleGroup(g.id)}
                  className="h-3.5 w-3.5 rounded border-[var(--line-strong,#d5d3cc)] text-[var(--accent,#a51c1c)] focus:ring-[var(--accent,#a51c1c)]"
                />
                {g.label}
              </label>
            );
          })}
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <SpinnerCustom />
          </div>
        ) : error ? (
          <div className="rounded-[10px] border border-[var(--crit-soft,#fbe6e6)] bg-white px-4 py-8 text-center text-[13px] text-[var(--crit,#c0262b)]">
            {error}
          </div>
        ) : viewMode === "provider" ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredLayouts.map((layout) => (
              <div
                key={`${layout.provider}-${layout.category}`}
                className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white overflow-hidden"
              >
                <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-[var(--line,#e4e2dc)]">
                  <div className="flex items-center gap-2 min-w-0">
                    <h3 className="text-[15px] font-semibold text-[var(--ink,#1c1c1a)] truncate">
                      {layout.provider}
                    </h3>
                    <span className="inline-flex rounded-full bg-[var(--sunken,#eeece7)] px-2 py-0.5 text-[11px] text-[var(--muted,#6b6a66)]">
                      {layout.category}
                    </span>
                  </div>
                  <span className="text-[12px] text-[var(--muted,#6b6a66)] whitespace-nowrap">
                    {layout.rows.length} columns
                  </span>
                </div>
                <div className="px-4 py-2 grid grid-cols-[1fr_auto_1fr] gap-x-3 text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                  <span>File column</span>
                  <span />
                  <span>App field</span>
                </div>
                <ul>
                  {layout.rows.map((row, i) => (
                    <li
                      key={`${row.input}-${row.field}-${i}`}
                      className="grid grid-cols-[1fr_auto_1fr] gap-x-3 items-center px-4 py-2 border-t border-[var(--line,#e4e2dc)]"
                    >
                      <span className="text-[13px] font-medium text-[var(--ink,#1c1c1a)]">
                        {row.input}
                      </span>
                      <span className="text-[var(--faint,#9a9a95)]">→</span>
                      <span>
                        <span className={`inline-flex rounded-md px-1.5 py-0.5 text-[12px] font-medium ${pillClass(row.field)}`}>
                          {row.field}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white overflow-hidden">
            {groupedAppFields.map((section) => (
              <div key={section.id} className="border-b border-[var(--line,#e4e2dc)] last:border-b-0">
                <div className="px-5 py-3 bg-[var(--sunken2,#fbfaf8)]">
                  <h3 className="text-[15px] font-semibold text-[var(--ink,#1c1c1a)]">{section.label}</h3>
                </div>
                <ul>
                  {section.items.map(({ field, rows }) => (
                    <li
                      key={field}
                      className="flex flex-col sm:flex-row sm:items-start gap-3 px-5 py-3.5 border-t border-[var(--line,#e4e2dc)]"
                    >
                      <div className="w-full sm:w-44 shrink-0">
                        <span className={`inline-flex rounded-md px-1.5 py-0.5 text-[12px] font-medium ${pillClass(field)}`}>
                          {field}
                        </span>
                        <p className="mt-1 text-[12px] text-[var(--muted,#6b6a66)]">{humanFieldLabel(field)}</p>
                      </div>
                      <div className="flex flex-wrap gap-1.5 flex-1">
                        {rows.map((row, i) => (
                          <span
                            key={`${row.provider}-${row.category}-${row.input}-${i}`}
                            className="inline-flex items-center rounded-full border border-[var(--line,#e4e2dc)] bg-white px-2.5 py-1 text-[12px] text-[var(--ink,#1c1c1a)]"
                          >
                            {chipLabel(row, rows)}
                          </span>
                        ))}
                        {field === "country" && countryDefaultProviders.length > 0 ? (
                          <span className="inline-flex items-center rounded-full border border-dashed border-[var(--line-strong,#d5d3cc)] bg-[var(--sunken2,#fbfaf8)] px-2.5 py-1 text-[12px] text-[var(--muted,#6b6a66)]">
                            Defaults to US for {countryDefaultProviders.join(", ")}
                          </span>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {!groupedAppFields.length ? (
              <p className="px-5 py-10 text-center text-[13px] text-[var(--muted,#6b6a66)]">
                No app fields match the current filters.
              </p>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
